import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import http from "../../lib/http";
import { TaskApi } from "../../api";
import defaultAvatar from "../../assets/images/user/user.png";
import { profiles } from "../User/UserData/data";

const TaskContext = createContext(null);

const TASK_STORAGE_KEY = "pms-task-provider-tasks";

const readStoredTasks = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(TASK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.error("Task storage read error", error);
    return null;
  }
};

const writeStoredTasks = (tasks) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(TASK_STORAGE_KEY, JSON.stringify(tasks || []));
  } catch (error) {
    console.error("Task storage write error", error);
  }
};

export const useTasks = () => {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error("useTasks must be used within <TaskProvider>");
  return ctx;
};

export const useTasksOptional = () => useContext(TaskContext);

const initialGlobalTasks = [
  {
    id: "1",
    title: "Genel görev - Dashboard",
    startDate: "Yesterday at 09:40",
    deadline: "Yesterday at 12:00",
    relatedTo: "PMS Digital Exchange Sistemi",
    assignedTo: "Sefa Demirci",
    status: "In progress",
    labels: ["Yazılım"],
    priority: "High",
    points: 2,
    userName: "Ömer Sefa Demirci",
    userImg: "https://i.pravatar.cc/80?img=12",
    createdTime: "14:31",
  },
  {
    id: "2",
    title: "SEO kontrol listesi",
    startDate: "Today 09:00",
    deadline: "Today 18:00",
    relatedTo: "Acme Corp",
    assignedTo: "Zehra Ecer",
    status: "To Do",
    labels: ["Analiz"],
    priority: "Medium",
    points: 1,
    userName: "Zehra Ecer",
    userImg: "https://i.pravatar.cc/80?img=30",
  },
  {
    id: "3",
    title: "Landing page tasarım revizyonu",
    startDate: "Tomorrow 10:00",
    deadline: "Tomorrow 17:00",
    relatedTo: "UI/UX",
    assignedTo: "Elif Yıldız",
    status: "Admin Approval",
    labels: ["Design"],
    priority: "High",
    points: 3,
    userName: "Elif Yıldız",
    userImg: "https://i.pravatar.cc/80?img=45",
  },
  {
    id: "4",
    title: "Müşteri sunumu",
    startDate: "21-08-2025 13:00",
    deadline: "21-08-2025 16:00",
    relatedTo: "Acme Corp",
    assignedTo: "Barış Korkmaz",
    status: "Customer Approval",
    labels: ["CRM"],
    priority: "Middle",
    points: 2,
    userName: "Barış Korkmaz",
    userImg: "https://i.pravatar.cc/80?img=31",
  },
  {
    id: "5",
    title: "Altyapı optimizasyonu",
    startDate: "Last week",
    deadline: "Next week",
    relatedTo: "PMS",
    assignedTo: "Dev2 Admin",
    status: "Done",
    labels: ["Yazılım"],
    priority: "Low",
    points: 1,
    userName: "Dev2 Admin",
    userImg: defaultAvatar,
  },
];

const initialTasksByScope = {
  ticket: {
    _default: initialGlobalTasks,
    79: [
      {
        id: "t-79-1",
        title: "Ticket #79 - Brief kontrol",
        startDate: "Today at 09:00",
        deadline: "Today at 17:00",
        relatedTo: "Ticket #79",
        assignedTo: "Merve Özkanak",
        status: "To Do",
        labels: ["Support"],
        priority: "Medium",
        points: 1,
      },
    ],
  },
  invoice: {
    _default: initialGlobalTasks,
    114: [
      {
        id: "inv-114-1",
        title: "Invoice #114 - Fiyat doğrulama",
        startDate: "Yesterday",
        deadline: "Tomorrow",
        relatedTo: "Invoice #114",
        assignedTo: "Zehra Ecer",
        status: "In progress",
        labels: ["Finance"],
        priority: "High",
        points: 2,
      },
    ],
  },
  project: { _default: initialGlobalTasks },
  contract: { _default: initialGlobalTasks },
  proposal: { _default: initialGlobalTasks },
  order: { _default: initialGlobalTasks },
  lead: { _default: initialGlobalTasks },
  client: { _default: initialGlobalTasks },
};

export default function TaskProvider({ children }) {
  const [tasksByScope, setTasksByScope] = useState(initialTasksByScope);
  const [tasks, setTasksState] = useState(
    () => readStoredTasks() || initialGlobalTasks
  );
  const [activitiesByTask, setActivitiesByTask] = useState({});
  const activitiesSeededRef = useRef(false);

  // API'den taskleri çek
  const fetchTasks = async () => {
    try {
      const resp = await http.get(TaskApi);
      setTasks(resp.data);
    } catch (error) {
      console.error("Task fetch error", error);
    }
  };

  useEffect(() => {
    // initialGlobalTasks modul-seviyesinde sabit; runtime'da değişmez. Yani
    // koşul mount'ta bir kere değerlendirilir. Eski kodda dep [tasks] idi —
    // her tasks mutation'ında effect gereksiz yere re-run ediyordu.
    if (!initialGlobalTasks.length) fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- CRUD helpers ----
  const getTasksFor = (scope, entityId) => {
    if (!scope || !entityId) return tasks;
    const scoped = tasksByScope[scope] || {};
    return scoped[entityId] || scoped._default || tasks;
  };

  // --- Activity helpers ---
  // Aktör her zaman user tipinden; client/lead karışıklığını önlemek için
  const pickActor = (task, actor = {}) => {
    let name = actor.name || task?.userName || "Dev2 Admin";
    let avatar = actor.avatar || task?.userImg || defaultAvatar;
    let actorId = actor.id || null;
    const userPool = profiles.filter((p) => p.type === "user" && p.overview);

    if (!actorId && userPool.length) {
      const randomProfile = userPool[Math.floor(Math.random() * userPool.length)];
      actorId = randomProfile.id;
      name = randomProfile.overview?.name || name;
      avatar = randomProfile.overview?.avatar || avatar;
    }

    return { name, avatar, id: actorId, entity: "user" };
  };

  const logActivity = (task, { fromStatus, toStatus, actor } = {}) => {
    if (!task?.id || !fromStatus || !toStatus || fromStatus === toStatus) {
      return;
    }
    const resolvedActor = pickActor(task, actor);
    const entry = {
      id: `${task.id}-${Date.now()}`,
      taskId: task.id,
      actorName: resolvedActor.name,
      actorAvatar: resolvedActor.avatar,
      actorId: resolvedActor.id,
      actorEntity: resolvedActor.entity,
      fromStatus,
      toStatus,
      at: new Date().toISOString(),
    };
    setActivitiesByTask((prev) => {
      const list = prev[task.id] || [];
      return { ...prev, [task.id]: [entry, ...list] };
    });
  };

  const diffAndLogStatus = (prevList, nextList) => {
    const prevMap = new Map((prevList || []).map((t) => [t.id, t]));
    (nextList || []).forEach((next) => {
      const prev = prevMap.get(next.id);
      if (!prev) return;
      if (prev.status !== next.status) {
        logActivity(next, { fromStatus: prev.status, toStatus: next.status });
      }
    });
  };

  // Mock activity oluşturucu (başlangıç için)
  const createMockActivities = (taskList) => {
    const statusTrail = ["To Do", "In progress", "Admin Approval", "Done"];
    const base = {};
    taskList.forEach((task, idx) => {
      const items = [];
      let prev = statusTrail[0];
      for (let i = 1; i < statusTrail.length; i++) {
        const to = statusTrail[i];
        const userPool = profiles.filter((p) => p.type === "user" && p.overview);
        const actorProfile = userPool[Math.floor(Math.random() * userPool.length)] || {};
        const actor = {
          id: actorProfile.id,
          entity: "user",
          name: actorProfile.overview?.name,
          avatar: actorProfile.overview?.avatar,
        };
        items.push({
          id: `${task.id}-mock-${i}`,
          taskId: task.id,
          actorName: actor.name || task.userName || "Dev2 Admin",
          actorAvatar: actor.avatar || task.userImg || defaultAvatar,
          actorId: actor.id || null,
          actorEntity: actor.entity || null,
          fromStatus: prev,
          toStatus: to,
          at: new Date(Date.now() - (idx * 4 + i) * 60 * 60 * 1000).toISOString(),
        });
        prev = to;
      }
      base[task.id] = items.reverse(); // en yeni üstte için tersine çevireceğiz zaten
    });
    return base;
  };

  // setTasks wrapper: log status değişimleri
  const setTasks = (updater) => {
    setTasksState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater ?? [];
      writeStoredTasks(next);
      diffAndLogStatus(prev, next);
      return next;
    });
  };

  // İlk yüklemede mock activity doldur
  useEffect(() => {
    if (activitiesSeededRef.current) return;
    activitiesSeededRef.current = true;
    setActivitiesByTask((prev) => {
      if (Object.keys(prev || {}).length > 0) return prev;
      return createMockActivities(tasks);
    });
  }, [tasks]);

  const setTasksFor = (scope, entityId, updater) => {
    if (!scope || !entityId) {
      setTasks(typeof updater === "function" ? updater : updater ?? []);
      return;
    }
    setTasksByScope((prev) => {
      const scoped = prev[scope] || {};
      const current = scoped[entityId] || [];
      const nextList =
        typeof updater === "function" ? updater(current) : updater ?? [];
      diffAndLogStatus(current, nextList);
      return {
        ...prev,
        [scope]: { ...scoped, [entityId]: nextList },
      };
    });
  };

  const addTask = (task, scope, entityId) => {
    const id = task.id || String(Date.now());
    const next = { ...task, id };
    if (scope && entityId) {
      setTasksFor(scope, entityId, (list) => [...list, next]);
    } else {
      setTasks((prev) => [...prev, next]);
    }
    return next;
  };

  const updateTask = (updated, scope, entityId) => {
    if (scope && entityId) {
      setTasksFor(scope, entityId, (list) =>
        list.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
      );
    } else {
      setTasks((prev) =>
        prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
      );
    }
  };

  const deleteTask = (id, scope, entityId) => {
    if (scope && entityId) {
      setTasksFor(scope, entityId, (list) => list.filter((t) => t.id !== id));
    } else {
      setTasks((prev) => prev.filter((t) => t.id !== id));
    }
  };

  const setStatus = (id, status, scope, entityId) => {
    if (scope && entityId) {
      setTasksFor(scope, entityId, (list) =>
        list.map((t) => (t.id === id ? { ...t, status } : t))
      );
    } else {
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    }
  };

  const value = useMemo(
    () => ({
      tasks,
      tasksByScope,
      activitiesByTask,
      setTasks,
      setTasksFor,
      addTask,
      updateTask,
      deleteTask,
      setStatus,
      fetchTasks,
      getTasksFor,
      getActivities: (taskId) => activitiesByTask[taskId] || [],
      logActivity,
    }),
    [tasks, tasksByScope, activitiesByTask]
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}
